// components/modals/invitations/user/project-access-table.jsx
import { Row, Col, Select, Button } from "antd";
import UserActivityStatus from "../../../../components/user-activity-status/user-activity-status";
import TagsSelect from "../../../tags-select/tags-select";
import { Icon } from "../../../icon/icon";
import { siteTagsFor } from "./use-project-sites";
import { coversWholeProject, roleOptionsFor, rowRoleOf } from "./role-options";

/**
 * Why a row has no site picker, in muted text — a bare dash beside another
 * row's "+ Add Site" read as the same field in two states.
 */
export const SitesNote = ({ children }) => (
  <div className="invite-user__sites-note">{children}</div>
);

/**
 * A row's sites, picked the way a site picks its subjects and users. A role
 * covering the whole project — a Project admin's, or a custom one like it — is
 * never limited by sites, so there is nothing to pick for it.
 */
export const RowSites = ({ row, sitesFor, updateRow, readOnly }) => {
  if (coversWholeProject(row)) {
    return <SitesNote>All sites</SitesNote>;
  }

  const { offered, selected } = siteTagsFor(sitesFor(row.projectId), row.siteIds);

  if (readOnly && !selected.length) {
    return <SitesNote>No sites</SitesNote>;
  }

  return (
    <div className="invite-user__sites">
      <TagsSelect
        options={offered}
        value={selected}
        onChange={(tags) => updateRow(row.key, { siteIds: tags.map((tag) => tag.value) })}
        readonly={readOnly}
        placeholder="+ Add Site"
        selectPlaceholder="Select site"
        noMarginLeft
      />
    </div>
  );
};

const ProjectAccessTable = ({
  projectRows,
  sitesFor,                   // (projectId) => { sitesEnabled, options }
  roles = [],                 // the roles the signed-in user may give
  projectRoleDropdownKey,
  setProjectRoleDropdownKey,
  updateRow,
  getProjectTitle,
  removeProjectRow,
  readOnly = false,
}) => {
  if (!projectRows?.length) {
    return null;
  }

  // Each row follows its own project's sites setting; the column is there
  // when any of them has sites.
  const sitesEnabled = projectRows.some((row) => sitesFor(row.projectId).sitesEnabled);

  return (
    <>
      <div className="invite-user__project-header">
        <Row gutter={16}>
          <Col span={7}>
            <p className="invite-user__field__name">Project</p>
          </Col>

          <Col span={6}>
            <p className="invite-user__field__name">Role</p>
          </Col>

          {sitesEnabled && (
            <Col span={10}>
              <p className="invite-user__field__name">Sites</p>
            </Col>
          )}

          <Col span={1} />
        </Row>
      </div>

      {projectRows.map((row) => (
        <div key={row.key} className="invite-user__project-row">
          <Row gutter={16} align="middle">
            <Col span={7}>
              <div className="invite-user__project-name">
                {getProjectTitle(row.projectId)}
              </div>
            </Col>

            <Col span={6}>
              <div className="invite-user__project-role-wrapper">
                <button
                  type="button"
                  className="invite-user__role-trigger"
                  onClick={() => !readOnly && setProjectRoleDropdownKey(row.key)}
                  disabled={readOnly}
                >
                  {row.roleId ? (
                    <UserActivityStatus
                      userStatus={row.roleKey}
                      text={row.roleName}
                      fontSize={14}
                      style={{ whiteSpace: "nowrap" }}
                    />
                  ) : (
                    <span className="invite-user__role-placeholder">
                      Select role
                    </span>
                  )}

                  <span className="invite-user__role-arrow" />
                </button>

                <Select
                  className="invite-user__project-role-select"
                  variant="borderless"
                  value={row.roleId}
                  open={projectRoleDropdownKey === row.key}
                  onOpenChange={(open) => {
                    if (!readOnly) setProjectRoleDropdownKey(open ? row.key : null);
                  }}
                  onChange={(val) => {
                    updateRow(row.key, rowRoleOf(roles.find((role) => role.id === val)));
                    setProjectRoleDropdownKey(null);
                  }}
                  options={roleOptionsFor(roles, row)}
                  popupMatchSelectWidth={false}
                  disabled={readOnly}
                />
              </div>
            </Col>

            {sitesEnabled && (
              <Col span={10}>
                {sitesFor(row.projectId).sitesEnabled ? (
                  <RowSites
                    row={row}
                    sitesFor={sitesFor}
                    updateRow={updateRow}
                    readOnly={readOnly}
                  />
                ) : (
                  <SitesNote>Sites off</SitesNote>
                )}
              </Col>
            )}

            {/* The whole row goes: project, role and sites. A trash can, as
                the task form uses for removing a row, so it does not read as
                clearing the role beside it. */}
            <Col span={1} className="invite-user__project-remove-cell">
              {!readOnly && (
                <Button
                  type="text"
                  size="small"
                  className="invite-user__project-remove"
                  icon={<Icon name="delete" />}
                  aria-label={`Remove ${getProjectTitle(row.projectId)}`}
                  title="Remove from this project"
                  onClick={() => removeProjectRow(row.key)}
                />
              )}
            </Col>
          </Row>
        </div>
      ))}
    </>
  );
};

export default ProjectAccessTable;
